/*
 * Posture before/after — drag-to-compare.
 *
 * The whole widget is CSS: the top photo is clipped and the divider/handle are
 * positioned from the --pst-ba-pos custom property. This file does two things:
 *   1. copies the native range input's value into that property;
 *   2. plays a one-off "nudge" demo the first time the widget scrolls into view,
 *      so people realise the divider is draggable.
 *
 * Progressive enhancement: with JS off the CSS default (50%) still renders a
 * correct, half-and-half comparison, and the range stays keyboard operable.
 */
(function () {
  var ROOT_SELECTOR = '[data-pst-ba]';
  var RANGE_SELECTOR = '[data-pst-ba-range]';

  /* Nudge demo: right, left, back to centre — ~1.6s in total, once per load. */
  var DEMO_STOPS = [50, 68, 34, 50];
  var DEMO_DURATION = 1600;
  var DEMO_VISIBILITY = 0.4;
  /* Anything the user does kills the demo, so it can never fight a real drag. */
  var DEMO_ABORT_EVENTS = ['pointerdown', 'mousedown', 'touchstart', 'keydown', 'input'];

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function easeInOutSine(t) {
    return -(Math.cos(Math.PI * t) - 1) / 2;
  }

  function setUpDemo(root, range, update) {
    if (prefersReducedMotion()) return;
    if (typeof window.IntersectionObserver !== 'function') return;
    if (typeof window.requestAnimationFrame !== 'function') return;

    var frameId = null;
    var observer = null;
    var finished = false;

    function releaseHooks() {
      for (var i = 0; i < DEMO_ABORT_EVENTS.length; i++) {
        root.removeEventListener(DEMO_ABORT_EVENTS[i], abort, true);
      }
    }

    function settle() {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
        frameId = null;
      }
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      delete root.dataset.pstBaDemo;
    }

    function abort() {
      if (finished) return;
      finished = true;
      settle();
      releaseHooks();
      /*
       * The range value is written every frame, so the control and the visual
       * already agree — leaving the divider exactly where it is means nothing
       * jumps under the user's finger as they take over.
       */
      update();
    }

    function run() {
      var startedAt = null;
      var legs = DEMO_STOPS.length - 1;

      root.dataset.pstBaDemo = 'running';

      function step(now) {
        if (finished) return;

        if (startedAt === null) startedAt = now;

        var progress = Math.min((now - startedAt) / DEMO_DURATION, 1);
        var scaled = progress * legs;
        var leg = Math.min(Math.floor(scaled), legs - 1);
        var from = DEMO_STOPS[leg];
        var to = DEMO_STOPS[leg + 1];

        range.value = String(from + (to - from) * easeInOutSine(scaled - leg));
        update();

        if (progress < 1) {
          frameId = window.requestAnimationFrame(step);
          return;
        }

        finished = true;
        settle();
        releaseHooks();
      }

      frameId = window.requestAnimationFrame(step);
    }

    observer = new window.IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;

          observer.disconnect();
          observer = null;
          if (!finished) run();
          return;
        }
      },
      { threshold: DEMO_VISIBILITY }
    );

    /* Capture phase, so a drag on the range aborts before its own handler runs. */
    for (var i = 0; i < DEMO_ABORT_EVENTS.length; i++) {
      root.addEventListener(DEMO_ABORT_EVENTS[i], abort, true);
    }

    observer.observe(root);
  }

  function bind(root) {
    if (root.dataset.pstBaBound === 'true') return;

    var range = root.querySelector(RANGE_SELECTOR);
    if (!range) return;

    root.dataset.pstBaBound = 'true';

    function update() {
      root.style.setProperty('--pst-ba-pos', range.value + '%');
    }

    range.addEventListener('input', update);
    range.addEventListener('change', update);

    // Browsers restore the previous value on soft reload — sync straight away.
    update();

    setUpDemo(root, range, update);
  }

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var roots = context.querySelectorAll(ROOT_SELECTOR);

    for (var i = 0; i < roots.length; i++) {
      bind(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor re-renders the section markup on every change.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
